import { useMemo, useState } from "react";

import { useForm } from "react-hook-form";

import { Page, toastSuccess } from "@alpacahq/ui/old";

import { useGetOwnerDetails, useMutationRequest } from "~/v2/api/hooks";
import {
  setUserConfigurations,
  type UserConfigurations,
} from "~/v2/api/rest/account";
import { FullScreenSpinner } from "~/v2/components/common";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { useGetUserConfigurations } from "../../../../api/hooks/useGetConfigurations";
import ConfigurationForm, {
  type Fields,
  type FieldValue,
  fields,
} from "./configurationForm";
import { OptionSection } from "./optionSection";

/** Convert FieldValue to boolean, handling string "true"/"false" */
const toBoolean = (val: FieldValue): boolean | undefined => {
  if (val == null) {
    return undefined;
  }
  return typeof val === "boolean" ? val : val === "true";
};

/** Convert FieldValue to non-empty string or undefined */
const toStringOrUndefined = (val: FieldValue): string | undefined => {
  if (val == null || val === "") {
    return undefined;
  }
  return String(val);
};

export default function AccountConfigurations() {
  const [isDisabled, setIsDisabled] = useState(false);

  const { product, currentAccount, refetchCurrentAccount } =
    useAccountContext();

  // Account details are resolved to the live trading account inside the hook.
  const { details } = useGetOwnerDetails({
    options: {
      gcTime: 0,
    },
  });
  // fetch the user configurations when the account is loaded
  const { user_configurations, refetch: refetchUserConfigurations } =
    useGetUserConfigurations({
      product,
      accountID: currentAccount?.id || "",
      enabled: !!currentAccount?.id,
    });

  const formMethods = useForm<Fields>({
    mode: "all",
    defaultValues: useMemo(() => {
      const formValues = { ...user_configurations };
      // remove any field that isn't in the list of configuration properties
      Object.keys(formValues).forEach((key) => {
        if (!fields.includes(key as keyof Fields)) {
          delete formValues[key as keyof Fields];
        }
      });

      return formValues;
    }, [user_configurations]),
  });

  // patch account with new values
  const mutation = useMutationRequest((data: Partial<UserConfigurations>) =>
    setUserConfigurations(currentAccount?.id || "", product, data),
  );

  // handle form submission
  const onSubmit = (data: Partial<Fields>) => {
    // This is a temporary fix until we migrate all the account with trade_confirm_email
    // have "enabled" setting so we don't prevent user from submitting account config.
    if (data.trade_confirm_email === "enabled") {
      data.trade_confirm_email = "all";
    }

    // remove max option trading level from configuration submission until they are
    // approved to trade options
    if (!currentAccount?.options_approved_level) {
      delete data.max_options_trading_level;
    }

    setIsDisabled(true);

    const payload: Partial<UserConfigurations> = {
      max_margin_multiplier: toStringOrUndefined(data.max_margin_multiplier),
      max_options_trading_level:
        data.max_options_trading_level != null &&
        data.max_options_trading_level !== ""
          ? Number(data.max_options_trading_level)
          : undefined,
      suspend_trade: toBoolean(data.suspend_trade),
      trade_confirm_email: toStringOrUndefined(data.trade_confirm_email),
      fractional_trading: toBoolean(data.fractional_trading),
      no_shorting: toBoolean(data.no_shorting),
      ptp_no_exception_entry: toBoolean(data.ptp_no_exception_entry),
    };

    return mutation.mutate(payload, {
      onSuccess: () => {
        // show a success toast
        toastSuccess("Configuration saved", {
          autoClose: 5000,
        });
      },
      onSettled: () => {
        setIsDisabled(false);
        refetchCurrentAccount();
        // we refetch user configuration to set the latest default values
        // instead of using the returned values in onSettled because the
        // live endpoint does not return anything
        refetchUserConfigurations();
      },
    });
  };

  // show spinner until account is loaded
  if (!user_configurations) {
    // offset spinner by a negative Y margin to offset the topbar
    return <FullScreenSpinner className="mt-[92px]" />;
  }

  return (
    <Page
      title="Configuration"
      subtitle="Manage your account settings."
      className="p-8 pt-0 xs:pl-0 md:p-8 md:pt-0 md:pl-0"
    >
      <div className="max-w-96">
        <ConfigurationForm
          user_configurations={user_configurations}
          formMethods={formMethods}
          product={product}
          onSubmit={onSubmit}
          details={details}
        />

        <OptionSection onSubmit={onSubmit} isDisabled={isDisabled} />
      </div>
    </Page>
  );
}
